import React from 'react';
import messages from './contracts.messages';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Button, Icon, Skeleton } from '@iwoca/orion';
import styles from './contracts.module.css';
import { useStateKey } from '../../iwQueryClient/useStateKey';
import { useQuery } from '@tanstack/react-query';
import { fetchGetLoanDocuments } from '@iwoca/lapi-client/edge';
import {
    H1,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@ui';

export const Contracts = () => {
    const intl = useIntl();
    const stateKey = useStateKey();

    const { data: contracts, isLoading } = useQuery({
        queryKey: ['loan_documents', stateKey],
        queryFn: () => fetchGetLoanDocuments({ stateKey }),
        select: (response) => response.data.document_list
    });

    const getDocumentTypeMessage = (documentType: string) => {
        switch (documentType) {
            case 'agreement':
                return intl.formatMessage(messages.agreement);
            case 'guarantee':
                return intl.formatMessage(messages.guarantee);
            default:
                return intl.formatMessage(messages.otherDoc);
        }
    };

    return (
        <div className={styles.contractsPage} data-testid="contracts_page">
            <H1>
                <FormattedMessage {...messages.title} />
            </H1>

            <Table className="mx-auto my-0 max-w-[800px] text-m">
                <TableHeader>
                    <TableRow className="flex">
                        <TableHead className="w-full">
                            {intl.formatMessage(messages.dateColHeader)}
                        </TableHead>
                        <TableHead className="w-full">
                            {intl.formatMessage(messages.typeColHeader)}
                        </TableHead>
                        <TableHead className="w-full" />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {isLoading ? (
                        <>
                            {Array.from({ length: 3 }).map((e, i) => (
                                <SkeletonRow key={i} />
                            ))}
                        </>
                    ) : (
                        contracts?.map(
                            ({
                                date_signed,
                                document_type,
                                document_title,
                                document_id
                            }) => (
                                <TableRow
                                    key={document_id}
                                    className="flex min-w-[450px]"
                                >
                                    <TableCell className="flex w-full">
                                        <FormattedDate
                                            day="numeric"
                                            month="short"
                                            year="numeric"
                                            value={date_signed}
                                        />
                                    </TableCell>
                                    <TableCell className="flex w-full">
                                        <div className="flex flex-col">
                                            <span>
                                                {getDocumentTypeMessage(
                                                    document_type
                                                )}
                                            </span>

                                            <span className="text-s text-structure-40">
                                                {document_title}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="flex w-full justify-end">
                                        <Button
                                            download
                                            href={`/api/lending/edge/loan_documents/${stateKey}/signed/${document_id}/?file_format=pdf`}
                                        >
                                            <FormattedMessage
                                                {...messages.download}
                                            />
                                            <Icon icon="download" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )
                        )
                    )}
                </TableBody>
            </Table>
        </div>
    );
};

const SkeletonRow = () => (
    <TableRow className="flex w-full">
        <TableCell className="flex w-full justify-center">
            <Skeleton />
        </TableCell>
        <TableCell className="flex w-full justify-center">
            <Skeleton />
        </TableCell>
        <TableCell className="flex w-full justify-center">
            <Skeleton />
        </TableCell>
    </TableRow>
);
